/**
 * Search Screen Design Tokens
 * Premium legal-tech aesthetic matching the lawgee-landing website.
 * Provides theme-aware tokens for both light and dark mode.
 *
 * Usage:
 *   import { useSearchTokens } from '../searchDesignTokens'
 *   const dk = useSearchTokens()
 */

import { useTheme } from '@mui/material'

/* ─── Token sets ──────────────────────────────────────────────────────────── */

const darkTokens = {
  bg:            '#000000',
  surface:       '#0d0d0d',
  elevated:      '#111111',
  hover:         '#1a1a1a',

  gold:          '#c9a96e',
  goldSub:       'rgba(201,169,110,0.12)',
  goldBdr:       'rgba(201,169,110,0.25)',
  goldHover:     'rgba(201,169,110,0.20)',
  goldGlow:      'rgba(201,169,110,0.04)',

  text1:         '#ffffff',
  text2:         'rgba(255,255,255,0.55)',
  text3:         'rgba(255,255,255,0.35)',
  disabled:      'rgba(255,255,255,0.20)',

  border:        'rgba(255,255,255,0.08)',
  borderLight:   'rgba(255,255,255,0.04)',
  borderHover:   'rgba(255,255,255,0.15)',

  shadow:        '0 4px 24px rgba(0,0,0,0.6)',
  shadowHover:   '0 8px 40px rgba(0,0,0,0.7)',
  shadowCard:    '0 2px 8px rgba(0,0,0,0.5)',

  inputBg:       'rgba(255,255,255,0.03)',
  inputBgHover:  'rgba(255,255,255,0.06)',
  inputBorder:   'rgba(255,255,255,0.10)',

  chipBg:        'rgba(255,255,255,0.06)',
  chipBgActive:  '#c9a96e',
  chipText:      'rgba(255,255,255,0.65)',
  chipTextActive:'#000000',

  scrollThumb:   'rgba(255,255,255,0.10)',

  success:       '#22c55e',
  error:         '#f87171',
  warning:       '#fbbf24',
}

const lightTokens = {
  bg:            '#faf9f7',
  surface:       '#ffffff',
  elevated:      '#f5f4f1',
  hover:         '#f0efe9',

  gold:          '#b8923e',
  goldSub:       'rgba(184,146,62,0.08)',
  goldBdr:       'rgba(184,146,62,0.22)',
  goldHover:     'rgba(184,146,62,0.14)',
  goldGlow:      'rgba(184,146,62,0.03)',

  text1:         '#1a1a1a',
  text2:         '#666666',
  text3:         '#999999',
  disabled:      '#cccccc',

  border:        'rgba(0,0,0,0.08)',
  borderLight:   'rgba(0,0,0,0.04)',
  borderHover:   'rgba(0,0,0,0.15)',

  shadow:        '0 2px 12px rgba(0,0,0,0.06)',
  shadowHover:   '0 6px 24px rgba(0,0,0,0.10)',
  shadowCard:    '0 1px 4px rgba(0,0,0,0.04)',

  inputBg:       '#ffffff',
  inputBgHover:  '#faf9f7',
  inputBorder:   'rgba(0,0,0,0.12)',

  chipBg:        'rgba(184,146,62,0.08)',
  chipBgActive:  '#b8923e',
  chipText:      '#666666',
  chipTextActive:'#ffffff',

  scrollThumb:   'rgba(0,0,0,0.12)',

  success:       '#10b981',
  error:         '#ef4444',
  warning:       '#f59e0b',
}

/* ─── Hook ────────────────────────────────────────────────────────────────── */

export const useSearchTokens = () => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  return isDark ? darkTokens : lightTokens
}

export default useSearchTokens
